/**
 * 文档菜单 Web Component
 * 功能：
 * - 三级菜单展开/收起
 * - 点击任意级别菜单可选中
 * - 派发自定义事件通知其他组件
 * - 键盘导航支持
 * - 移动端下拉选择器模式
 * 
 * 事件：
 * - doc-menu:select - 选中菜单项时触发，detail: { id, level, title }
 */

(function() {
  'use strict';

  class DocMenu extends HTMLElement {
    constructor() {
      super();
      this.isMobile = false;
      this.currentTitle = '请选择';
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.checkMobile();
      this.bindToggleEvents();
      this.bindLinkEvents();
      this.bindKeyboardNav();
      this.bindMobileEvents();
      
      // 监听窗口大小变化
      window.addEventListener('resize', () => this.checkMobile());
      
      // 设置第一个菜单项为默认选中状态
      this.setDefaultActiveItem();
    }

    // 设置默认选中项
    setDefaultActiveItem() {
      const firstToggle = this.querySelector('.doc-menu__level-1 .doc-menu__toggle');
      
      if (firstToggle) {
        const title = firstToggle.querySelector('.doc-menu__title')?.textContent?.trim();
        
        // 设置选中状态
        this.setActiveItem(firstToggle);
        
        // 更新移动端头部标题
        this.updateMobileTitle(title);
      }
    }

    // 检测是否为移动端
    checkMobile() {
      this.isMobile = window.innerWidth < 768;
    }

    // 绑定移动端事件
    bindMobileEvents() {
      // 头部点击展开/收起
      const mobileHeader = this.querySelector('.doc-menu__mobile-header');
      if (mobileHeader) {
        mobileHeader.addEventListener('click', () => {
          this.toggleMobileMenu();
        });
      }

      // 确认按钮点击关闭
      const confirmBtn = this.querySelector('.doc-menu__mobile-confirm');
      if (confirmBtn) {
        confirmBtn.addEventListener('click', () => {
          this.closeMobileMenu();
        });
      }

      // 遮罩层点击关闭
      const overlay = this.querySelector('.doc-menu__overlay');
      if (overlay) {
        overlay.addEventListener('click', () => {
          this.closeMobileMenu();
        });
      }
    }

    // 切换移动端菜单
    toggleMobileMenu() {
      if (this.classList.contains('is-mobile-open')) {
        this.closeMobileMenu();
      } else {
        this.openMobileMenu();
      }
    }

    // 打开移动端菜单
    openMobileMenu() {
      this.classList.add('is-mobile-open');
      document.body.style.overflow = 'hidden';
    }

    // 关闭移动端菜单
    closeMobileMenu() {
      this.classList.remove('is-mobile-open');
      document.body.style.overflow = '';
    }

    // 更新移动端头部标题
    updateMobileTitle(title) {
      const mobileTitle = this.querySelector('.doc-menu__mobile-title');
      if (mobileTitle && title) {
        mobileTitle.textContent = title;
        this.currentTitle = title;
      }
    }

    // 绑定展开/收起事件（一二级菜单）
    bindToggleEvents() {
      const toggles = this.querySelectorAll('.doc-menu__toggle');
      
      toggles.forEach(toggle => {
        toggle.addEventListener('click', (e) => {
          e.preventDefault();
          this.toggleItem(toggle);
          
          // 设置选中状态
          this.setActiveItem(toggle);
          
          // 派发事件
          const docId = toggle.getAttribute('data-doc-id');
          const level = toggle.closest('[data-level]')?.getAttribute('data-level');
          const title = toggle.querySelector('.doc-menu__title')?.textContent?.trim();
          const searchQuery = toggle.getAttribute('data-search-query');
          this.dispatchSelectEvent(docId, level, title, searchQuery);
          
          // 更新移动端头部标题
          this.updateMobileTitle(title);
        });
      });
    }

    // 切换展开/收起状态
    toggleItem(toggle) {
      const parent = toggle.parentElement;
      
      // 如果没有子级，不进行展开/收起操作
      if (parent.classList.contains('no-children')) {
        return;
      }
      
      const isExpanded = parent.classList.contains('is-expanded');
      
      if (isExpanded) {
        this.collapseItem(parent);
      } else {
        this.expandItem(parent);
      }
    }

    // 展开菜单项
    expandItem(item) {
      const children = item.querySelector('.doc-menu__children');
      const toggle = item.querySelector('.doc-menu__toggle');
      
      if (!children) return;
      
      item.classList.add('is-expanded');
      toggle.setAttribute('aria-expanded', 'true');
      
      // 动画展开
      children.style.height = '0';
      children.style.overflow = 'hidden';
      children.style.display = 'block';
      
      const height = children.scrollHeight;
      children.style.transition = 'height 0.3s ease';
      children.style.height = height + 'px';
      
      children.addEventListener('transitionend', function handler() {
        children.style.height = '';
        children.style.overflow = '';
        children.style.transition = '';
        children.removeEventListener('transitionend', handler);
      });
    }

    // 收起菜单项
    collapseItem(item) {
      const children = item.querySelector('.doc-menu__children');
      const toggle = item.querySelector('.doc-menu__toggle');
      
      if (!children) return;
      
      item.classList.remove('is-expanded');
      toggle.setAttribute('aria-expanded', 'false');
      
      // 动画收起
      const height = children.scrollHeight;
      children.style.height = height + 'px';
      children.style.overflow = 'hidden';
      children.style.transition = 'height 0.3s ease';
      
      // 强制重绘
      children.offsetHeight;
      
      children.style.height = '0';
      
      children.addEventListener('transitionend', function handler() {
        children.style.display = 'none';
        children.style.height = '';
        children.style.overflow = '';
        children.style.transition = '';
        children.removeEventListener('transitionend', handler);
      });
    }

    // 绑定文档链接点击事件（三级菜单）
    bindLinkEvents() {
      const links = this.querySelectorAll('.doc-menu__link');
      
      links.forEach(link => {
        link.addEventListener('click', (e) => {
          e.preventDefault();
          
          const docId = link.getAttribute('data-doc-id');
          const level = link.closest('[data-level]')?.getAttribute('data-level');
          const title = link.textContent?.trim();
          const searchQuery = link.getAttribute('data-search-query');
          
          this.setActiveItem(link);
          this.dispatchSelectEvent(docId, level, title, searchQuery);
          
          // 更新移动端头部标题
          this.updateMobileTitle(title);
        });
      });
    }

    // 派发选中事件
    dispatchSelectEvent(id, level, title, searchQuery) {
      const event = new CustomEvent('doc-menu:select', {
        bubbles: true,
        detail: { id, level, title, searchQuery }
      });
      this.dispatchEvent(event);
    }

    // 设置当前激活的项（支持一二三级）
    setActiveItem(activeElement) {
      // 移除所有激活状态（一次性选择所有可能有激活状态的元素）
      this.querySelectorAll('.is-active').forEach(el => {
        el.classList.remove('is-active');
      });
      
      // 设置当前激活
      activeElement.classList.add('is-active');
    }

    // 键盘导航
    bindKeyboardNav() {
      this.addEventListener('keydown', (e) => {
        const activeElement = document.activeElement;
        
        if (!this.contains(activeElement)) return;
        
        switch(e.key) {
          case 'Enter':
          case ' ':
            if (activeElement.classList.contains('doc-menu__toggle')) {
              e.preventDefault();
              this.toggleItem(activeElement);
              
              // 设置选中状态
              this.setActiveItem(activeElement);
              
              // 派发事件
              const docId = activeElement.getAttribute('data-doc-id');
              const level = activeElement.closest('[data-level]')?.getAttribute('data-level');
              const title = activeElement.querySelector('.doc-menu__title')?.textContent?.trim();
              this.dispatchSelectEvent(docId, level, title);
              
              // 更新移动端头部标题
              this.updateMobileTitle(title);
            }
            break;
            
          case 'ArrowDown':
            e.preventDefault();
            this.focusNextItem(activeElement);
            break;
            
          case 'ArrowUp':
            e.preventDefault();
            this.focusPrevItem(activeElement);
            break;
            
          case 'ArrowRight':
            if (activeElement.classList.contains('doc-menu__toggle')) {
              const parent = activeElement.parentElement;
              // 有子级且未展开时才展开
              if (!parent.classList.contains('no-children') && !parent.classList.contains('is-expanded')) {
                e.preventDefault();
                this.expandItem(parent);
              }
            }
            break;
            
          case 'ArrowLeft':
            if (activeElement.classList.contains('doc-menu__toggle')) {
              const parent = activeElement.parentElement;
              // 有子级且已展开时才收起
              if (!parent.classList.contains('no-children') && parent.classList.contains('is-expanded')) {
                e.preventDefault();
                this.collapseItem(parent);
              }
            }
            break;
            
          case 'Escape':
            // ESC 关闭移动端菜单
            if (this.isMobile && this.classList.contains('is-mobile-open')) {
              e.preventDefault();
              this.closeMobileMenu();
            }
            break;
        }
      });
    }

    // 聚焦下一个可聚焦元素
    focusNextItem(current) {
      const focusables = Array.from(this.querySelectorAll('.doc-menu__toggle, .doc-menu__link'));
      const currentIndex = focusables.indexOf(current);
      const nextIndex = (currentIndex + 1) % focusables.length;
      focusables[nextIndex]?.focus();
    }

    // 聚焦上一个可聚焦元素
    focusPrevItem(current) {
      const focusables = Array.from(this.querySelectorAll('.doc-menu__toggle, .doc-menu__link'));
      const currentIndex = focusables.indexOf(current);
      const prevIndex = (currentIndex - 1 + focusables.length) % focusables.length;
      focusables[prevIndex]?.focus();
    }
  }

  // 注册自定义元素
  if (!customElements.get('doc-menu')) {
    customElements.define('doc-menu', DocMenu);
  }
})();
